import React from "react";
import { RowFormText } from "../Common/Forms/FormText";
import EntityModal, { ModalMode } from "../Common/EntityModal";
import { CarrierDto } from "../../requests/useGetCarriers";
import useUpsertCarrier, { CarrierUpsertDto } from "../../requests/useUpsertCarrier";
import useGetCarrierSiteAssignment from "../../requests/useGetCarrierSiteAssignment";
import useEntityForm from "../Common/useEntityForm";
import { FormSiteAssignment } from "../Common/SiteAssignment/FormSiteAssignment";
import { RowHooksFormTextArea } from "../Common/Forms/FormTextArea";
import { FormEmailList, fromEmailFields, toEmailFields } from "../Common/Forms/FormEmailList";

type CarrierForm = Omit<CarrierUpsertDto, 'emailAddresses'> & {
  emailAddresses: { email: string }[]
}

interface CreateEditCarrierProps {
  initialCarrier: CarrierDto | null;
  onDismiss: () => void;
  onUpsert: (carrierId: string, carrierName: string) => void;
  mode: ModalMode;
}

export const CreateEditCarrier = (props: CreateEditCarrierProps) => {
  const { id, sites: initialSites, emailAddresses: initialEmails, ...initialCarrier } = { ...props.initialCarrier };

  const { hookProps, control, trigger, submit, isSaving, loading, loadError, displayMessage, sites } =
    useEntityForm<CarrierForm, CarrierUpsertDto>({
      initialId: props.initialCarrier?.id,
      defaultValues: {
        ...initialCarrier,
        siteIds: [],
        emailAddresses: toEmailFields(initialEmails),
      },
      useSiteAssignment: options => useGetCarrierSiteAssignment(props.initialCarrier?.id, options),
      useUpsert: options => useUpsertCarrier(props.initialCarrier?.id, options),
      toDto: form => ({
        ...form,
        emailAddresses: fromEmailFields(form.emailAddresses),
      }),
      onSuccess: (carrierId, form) => {
        props.onDismiss();
        props.onUpsert(carrierId, form.name);
      },
    });

  return <EntityModal
    mode={props.mode}
    entityName="carrier"
    displayMessage={displayMessage}
    loading={loading}
    loadError={loadError}
    renderSiteAssignment={<FormSiteAssignment {...hookProps('siteIds')} control={control} sites={sites} />}
    onHide={props.onDismiss}
    isSaving={isSaving}
    onSubmit={submit}
    renderForm={(<>
      <RowFormText {...hookProps('name')}
        options={{
          required: 'Name is required',
          validate: { length: v => v.length < 51 || 'Name cannot be longer than 50 characters' }
        }} />
      <RowFormText {...hookProps('number')}
        displayName="Carrier Number"
        options={{
          validate: { length: v => v.length < 51 || 'Number cannot be longer than 50 characters' }
        }} />
      <RowFormText {...hookProps("addressStreet")}
        displayName="Street"
        options={{
          validate: {
            length: v => v.length < 51 || 'Address Street cannot be longer than 50 characters'
          }
        }} />
      <RowFormText {...hookProps("addressCity")}
        displayName="City"
        options={{
          validate: {
            length: v => v.length < 51 || 'Address City cannot be longer than 50 characters'
          }
        }} />
      <RowFormText {...hookProps("addressState")}
        displayName="State"
        options={{
          validate: {
            length: v => v.length < 51 || 'Address State cannot be longer than 50 characters'
          }
        }} />
      <RowFormText {...hookProps("addressPostalCode")}
        displayName="Postal Code"
        options={{
          validate: {
            length: v => v.length < 16 || 'Address Postal Code cannot be longer than 15 characters'
          }
        }} />
      <RowFormText {...hookProps("addressCountry")}
        displayName="Country"
        options={{
          validate: {
            length: v => v.length < 101 || 'Address Country cannot be longer than 100 characters'
          }
        }} />
      <RowFormText {...hookProps('phone')}
        options={{
          validate: { length: v => v.length < 51 || 'Phone cannot be longer than 50 characters' }
        }} />
      <FormEmailList {...hookProps('emailAddresses')}
        control={control}
        trigger={trigger}
        disabled={props.mode === ModalMode.View}
        max={10} />
      <RowHooksFormTextArea {...hookProps("notes")} />
    </>)} />
};
